import axiosInstance from '../../../api/axios';

/**
 * AI Assistant API client.
 * The backend resolves the active workspace from the authenticated user; when a
 * workspaceId is supplied it is sent as x-workspace-id so the server can reject
 * mismatches instead of silently using a different workspace.
 */
const withWorkspace = (workspaceId) =>
  workspaceId ? { headers: { 'x-workspace-id': workspaceId } } : {};

const assistantApi = {
  async getConfig(workspaceId) {
    const res = await axiosInstance.get('/assistant/config', withWorkspace(workspaceId));
    return res.data?.data || null;
  },

  async listCommands(workspaceId) {
    const res = await axiosInstance.get('/assistant/commands', withWorkspace(workspaceId));
    return res.data?.data || [];
  },

  async listRooms(workspaceId) {
    const res = await axiosInstance.get('/assistant/rooms', withWorkspace(workspaceId));
    return res.data?.data || [];
  },

  async createRoom(workspaceId, name) {
    const res = await axiosInstance.post(
      '/assistant/rooms',
      name ? { name } : {},
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  async renameRoom(workspaceId, roomId, name) {
    const res = await axiosInstance.patch(
      `/assistant/rooms/${roomId}`,
      { name },
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  async deleteRoom(workspaceId, roomId) {
    const res = await axiosInstance.delete(
      `/assistant/rooms/${roomId}`,
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  async getMessages(workspaceId, roomId) {
    const res = await axiosInstance.get(
      `/assistant/rooms/${roomId}/messages`,
      withWorkspace(workspaceId)
    );
    return res.data?.data || { room: null, messages: [] };
  },

  async sendMessage(workspaceId, roomId, content) {
    const res = await axiosInstance.post(
      `/assistant/rooms/${roomId}/messages`,
      { content },
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  async generateReport(workspaceId, { roomId, aspect = 'overall', periodDays = 30 } = {}) {
    const res = await axiosInstance.post(
      '/assistant/reports/generate',
      { roomId, aspect, periodDays },
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  async downloadReport(workspaceId, reportId, filename = 'analysis-report.pdf') {
    const res = await axiosInstance.get(`/assistant/reports/${reportId}/download`, {
      ...withWorkspace(workspaceId),
      responseType: 'blob',
    });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
    return true;
  },
};

/** Extract a safe, user-facing error message from an axios error. */
export function getAssistantErrorMessage(
  error,
  fallback = 'Something went wrong. Please try again.'
) {
  return error?.response?.data?.error || fallback;
}

export default assistantApi;
